import { useState, useRef } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, Paperclip, X, CheckCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SupportPage() {
  const [formData, setFormData] = useState({ name: "", school_name: "", email: "", phone: "", subject: "", description: "", priority: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const handleSupportClick = () => {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!formData.name.trim() || !formData.school_name.trim() || !formData.email.trim() || !formData.subject.trim() || !formData.description.trim() || !formData.priority) {
      setError("Prosím vyplňte všechna povinná pole.");
      return;
    }
    if (!formData.email.includes("@")) {
      setError("Prosím zadejte platný email.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", formData.name);
      fd.append("school_name", formData.school_name);
      fd.append("email", formData.email);
      fd.append("phone", formData.phone);
      fd.append("subject", formData.subject);
      fd.append("description", formData.description);
      fd.append("priority", formData.priority);
      if (file) fd.append("attachment", file);
      await axios.post(`${API}/support-request`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      setError("Něco se nepovedlo. Zkuste to prosím znovu.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", school_name: "", email: "", phone: "", subject: "", description: "", priority: "" });
    setFile(null);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header onOrderClick={() => {}} onSupportClick={handleSupportClick} />

      {/* Success overlay */}
      {showSuccess && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-10 max-w-md text-center shadow-2xl">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-5" />
            <h2 className="text-2xl font-bold text-[#0F1B3D] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Děkujeme za Váš požadavek
            </h2>
            <p className="text-[#4A5B7A] text-lg">Začínáme se jím zabývat.</p>
          </div>
        </div>
      )}

      <main className="flex-1 pt-28 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0F1B3D] mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Kontaktujte podporu
          </h1>
          <p className="text-[#4A5B7A] mb-10" style={{ fontFamily: "'Inter', sans-serif" }}>
            Popište nám váš problém a my se jím začneme zabývat.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-[#0F1B3D]">Jméno a příjmení *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Jan Novák" className="rounded-xl border-[#E8EDF5] py-5" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-[#0F1B3D]">Jméno ZŠ/MŠ *</Label>
              <Input value={formData.school_name} onChange={(e) => setFormData({ ...formData, school_name: e.target.value })} placeholder="ZŠ Příkladová" className="rounded-xl border-[#E8EDF5] py-5" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-[#0F1B3D]">E-mail *</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="jan@skola.cz" className="rounded-xl border-[#E8EDF5] py-5" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-[#0F1B3D]">Telefon <span className="text-[#9CA3AF] font-normal">(nepovinné)</span></Label>
              <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+420..." className="rounded-xl border-[#E8EDF5] py-5" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-[#0F1B3D]">S čím potřebujete pomoci *</Label>
              <Input value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} placeholder="Stručný popis" className="rounded-xl border-[#E8EDF5] py-5" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-[#0F1B3D]">Popis problému *</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Popište podrobně, co se děje..." className="rounded-xl border-[#E8EDF5] min-h-[120px]" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-[#0F1B3D]">Priorita *</Label>
              <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                <SelectTrigger className="rounded-xl border-[#E8EDF5] py-5">
                  <SelectValue placeholder="Vyberte prioritu" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">
                    <div><span className="font-bold">1 — Mám dotaz</span><br /><span className="text-xs text-[#6B7A99]">Chci se na něco zeptat nebo si nejsem jistý.</span></div>
                  </SelectItem>
                  <SelectItem value="2">
                    <div><span className="font-bold">2 — Potřebuji pomoc</span><br /><span className="text-xs text-[#6B7A99]">Něco nefunguje podle očekávání nebo si nevím rady.</span></div>
                  </SelectItem>
                  <SelectItem value="3">
                    <div><span className="font-bold">3 — Nemohu pracovat</span><br /><span className="text-xs text-[#6B7A99]">Problém mi brání pokračovat v práci.</span></div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-[#0F1B3D]">Přiložit soubor <span className="text-[#9CA3AF] font-normal">(screenshot, Word, PDF...)</span></Label>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#E8EDF5] text-sm text-[#3D4F6F] hover:bg-[#F8FAFF] transition-colors">
                  <Paperclip className="w-4 h-4" /> Vybrat soubor
                </button>
                {file && (
                  <div className="flex items-center gap-2 text-sm text-[#0049B3]">
                    <span className="truncate max-w-[200px]">{file.name}</span>
                    <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}><X className="w-4 h-4 text-[#9CA3AF] hover:text-red-500" /></button>
                  </div>
                )}
                <input ref={fileRef} type="file" className="hidden" accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </div>
            </div>

            {error && <p className="text-sm text-red-500 font-medium">{error}</p>}

            <div className="flex gap-4 pt-4">
              <Button type="submit" disabled={loading} size="lg" className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold py-6 text-base">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Odeslat požadavek"}
              </Button>
              <Button type="button" size="lg" variant="outline" className="border-[#E8EDF5] text-[#6B7A99] rounded-xl font-semibold py-6 px-8" onClick={resetForm} disabled={loading}>
                Zrušit
              </Button>
            </div>
          </form>
        </div>
      </main>

      <Footer onSupportClick={handleSupportClick} />
    </div>
  );
}
